'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import styles from './auto-insurance-matches.module.scss';
import MatchesFilters from './components/matches-filters';
import MatchesOfferCard from './components/matches-offer-card';
import MatchesOfferCardSkeleton from './components/matches-offer-card-skeleton';
import ZipCodeInput from './components/zip-code-input';
import { updateFilter } from './utils';

const fetchMediaAlphaOffers = async (params: {
  zipCode: string;
  age?: string;
  interestedInHomeInsurance?: boolean;
  isHomeOwner?: boolean;
  isMarried?: boolean;
  currentInsurer?: string;
  url?: string;
  isVeteran?: boolean;
}) => {
  const searchParams = new URLSearchParams();

  // Add all parameters to search params
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const response = await fetch(
    `/api/insurance-matches-services?${searchParams.toString()}`
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch offers');
  }

  const data = await response.json();
  return data.offers;
};

const AutoInsuranceMatches = ({ zipCode }: { zipCode?: string }) => {
  const searchParams = useSearchParams();
  const queryZipCode = searchParams.get('zipCode');

  const [filters, setFilters] = useState({
    zipCode: queryZipCode || zipCode || '85266',
    age: '',
    insurer: '',
    isMarried: false,
    isHomeOwner: false,
    isVeteran: false,
    addHomeInsurance: false,
  });
  const [offers, setOffers] = useState<(any & { url: string })[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const baseUrl =
    typeof window !== 'undefined'
      ? window.location.origin
      : process.env.SITE_BASE_URL;

  const handleFetchOffers = useCallback(async () => {
    try {
      setOffers([]);
      setLoading(true);
      setError(null);
      setHasAttemptedFetch(true);

      const maAds = await fetchMediaAlphaOffers({
        zipCode: filters.zipCode,
        age: filters.age,
        interestedInHomeInsurance: filters.addHomeInsurance,
        isHomeOwner: filters.isHomeOwner,
        isMarried: filters.isMarried,
        currentInsurer: filters.insurer,
        url: baseUrl,
        isVeteran: filters.isVeteran,
      });

      const mappedResults = maAds.map((offer: any) => {
        const redirectUrl = new URL(
          `/insurance/auto/view-your-car-insurance-matches/redirect/${offer.id}`,
          baseUrl
        );
        redirectUrl.searchParams.set('destinationUrl', offer.clickUrl);
        return {
          ...offer,
          url: redirectUrl.toString(),
        };
      });
      setOffers(mappedResults);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching offers:', err);
      setError("Hmm, we didn't find results for that ZIP code.");
    } finally {
      setLoading(false);
    }
  }, [filters, baseUrl]);

  useEffect(() => {
    if (isZipCodeValid) {
      handleFetchOffers();
    }
  }, [isZipCodeValid, handleFetchOffers]);

  const hasAttemptedFetchAndNoOffers =
    hasAttemptedFetch && !error && !loading && !offers.length;

  const shouldRenderOffers =
    hasAttemptedFetch && !error && !loading && offers.length;

  return (
    <div className={styles.container}>
      <div className={styles.filtersContainer}>
        <ZipCodeInput
          initialZipCode={filters.zipCode}
          onZipCodeChange={(zip) =>
            setFilters((prev) => ({
              ...prev,
              zipCode: zip,
            }))
          }
          onZipCodeValidChange={setIsZipCodeValid}
        />
        <MatchesFilters
          filters={filters}
          updateFilter={(key, value) =>
            updateFilter(filters, setFilters, key, value)
          }
        />
      </div>
      {loading && <MatchesOfferCardSkeleton />}
      {error && (
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
      {hasAttemptedFetchAndNoOffers && (
        <div className={styles.noOffersContainer}>
          <p>No offers found for the provided information.</p>
        </div>
      )}
      {shouldRenderOffers && (
        <div className={styles.offersGrid}>
          {offers.map((offer, index) => {
            return (
              <MatchesOfferCard
                offer={offer}
                offerIndex={index}
                offerTotal={offers.length}
                key={offer.id}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AutoInsuranceMatches;
